import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { APP_RETURN_TIMEOUT, ApiStatus, ErrorCode } from 'Constants';
import isEqual from 'lodash-es/isEqual';
import { useAccount, useApiError } from 'Hooks';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import { logout } from 'Utilities/session';
import { store } from 'Utilities/store';
import { updatePatient } from 'Services/patient';
import AccountInfoAdult from './AccountInfoAdult';
import AccountInfoMinor from './AccountInfoMinor';
const mapStateToProps = ({ auth, config: { validation, minAge }, env: { osType, osVersion, app, appVersion }, }) => {
    return {
        patient: auth.include.patient,
        accountId: auth.account_id,
        firstNameLimit: validation.patient.firstNameMaxLength,
        lastNameLimit: validation.patient.lastNameMaxLength,
        osType,
        osVersion,
        app,
        appVersion,
        minAge,
    };
};
const connector = connect(mapStateToProps);
const AccountInformation = ({ patient, accountId, minAge, osType, osVersion, app, appVersion, ...rest }) => {
    const [loading, setLoading] = useState(false);
    const { validateIsMinor, validateDOBAndDisplayError, validateEntriesLengthAndDisplayError, showMinorRestrictionMessage, } = useAccount();
    const isMinor = validateIsMinor(patient.dateOfBirth, minAge);
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const handleApiRequestError = useCallback((error) => {
        switch (error.status) {
            case ApiStatus.UNAUTHORIZED:
                logout();
                break;
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [showApiErrorModal, showNetworkErrorModal]);
    const onSave = useCallback((patient) => {
        setLoading(true);
        updatePatient(accountId, patient, osType, osVersion, app, appVersion)
            .then(() => {
            notifyApp('adc-webview:update-account', {
                id: accountId,
                country: patient.country,
                language: patient.language,
                firstName: patient.firstName,
                lastName: patient.lastName,
                dateOfBirth: patient.dateOfBirth,
                email: patient.email,
                guardianFirstName: patient.guardianFirstName,
                guardianLastName: patient.guardianLastName,
            });
            // This timeout is added to give the app time to redirect the user back.
            setTimeout(() => {
                setLoading(false);
            }, APP_RETURN_TIMEOUT);
        })
            .catch((error) => {
            setLoading(false);
            handleApiRequestError(error);
        });
    }, [accountId, app, appVersion, handleApiRequestError, osType, osVersion]);
    const hasFormChanged = useCallback((initialValues, currentValues) => {
        return !isEqual(initialValues, currentValues);
    }, []);
    const showUnsavedChangesModal = () => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                header: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.title'),
                details: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.description'),
                primaryAction: {
                    label: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.keepEditingButton'),
                    onPress: () => {
                        store.dispatch({
                            type: CLOSE_MODAL,
                        });
                    },
                },
                secondaryAction: {
                    label: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.discardChangesButton'),
                    onPress: () => {
                        notifyApp('adc-webview:navigate-back-app');
                    },
                    destructive: true,
                },
                buttonLayout: 'column',
            },
        });
    };
    const onBack = useCallback((initialValues, currentValues) => {
        if (hasFormChanged(initialValues, currentValues)) {
            showUnsavedChangesModal();
            return;
        }
        notifyApp('adc-webview:navigate-back-app');
    }, [hasFormChanged]);
    return (React.createElement(React.Fragment, null, isMinor ? (React.createElement(AccountInfoMinor, { patient: patient, onSave: onSave, onBack: onBack, hasFormChanged: hasFormChanged, validateDOBAndDisplayError: validateDOBAndDisplayError, validateEntriesLengthAndDisplayError: validateEntriesLengthAndDisplayError, loading: loading, ...rest })) : (React.createElement(AccountInfoAdult, { patient: patient, app: app, onSave: onSave, onBack: onBack, hasFormChanged: hasFormChanged, loading: loading, minAge: minAge, validateDOBAndDisplayError: validateDOBAndDisplayError, validateEntriesLengthAndDisplayError: validateEntriesLengthAndDisplayError, validateIsMinor: validateIsMinor, showMinorRestrictionMessage: showMinorRestrictionMessage, ...rest }))));
};
export default connector(AccountInformation);
